import React, { useEffect } from 'react';
import AOS from 'aos';
import { motion } from 'framer-motion';
import { FaLaptopCode, FaMobileAlt, FaCloud, FaRobot, FaPalette, FaGlobe, FaWrench, FaImage, FaPenNib, FaMagic, FaBullhorn, FaIdBadge } from 'react-icons/fa';
import 'aos/dist/aos.css';

const services = [
  {
    icon: <FaLaptopCode aria-label="Web Development Icon" title="Web Development" />,
    title: 'Web Development',
    description: 'We build SEO-friendly, fast, and scalable websites using the latest technologies like React, Node.js, and cloud hosting. Designed to maximize performance, rankings, and conversions.',
  },
  {
    icon: <FaMobileAlt aria-label="App Development Icon" title="App Development" />,
    title: 'App Development',
    description: 'Cross-platform and native mobile apps with intuitive UI, robust security, and smooth performance—crafted for iOS, Android, and beyond.',
  },
  {
    icon: <FaGlobe aria-label="Web Applications Icon" title="Web Applications" />,
    title: 'Web Applications',
    description: 'Secure, custom web apps tailored for your business workflows. From dashboards to SaaS platforms, we deliver scalable solutions that grow with you.',
  },
  {
    icon: <FaWrench aria-label="Website Maintenance Icon" title="Website Maintenance" />,
    title: 'Website Maintenance',
    description: 'Ongoing website care including performance monitoring, updates, bug fixes, and security patches to ensure your site runs reliably 24/7.',
  },
  {
    icon: <FaPalette aria-label="Website Designing Icon" title="Website Designing" />,
    title: 'Website Designing',
    description: 'User-focused web design blending creativity and conversion-driven strategy. Modern, responsive, and visually stunning interfaces that engage visitors.',
  },
  {
    icon: <FaCloud aria-label="Cloud Solutions Icon" title="Cloud and SaaS Solutions" />,
    title: 'Cloud & SaaS Solutions',
    description: 'We create and manage cloud-powered SaaS applications that are scalable, secure, and optimized for modern enterprises.',
  },
  {
    icon: <FaRobot aria-label="AI ML Solutions Icon" title="AI and Machine Learning Solutions" />,
    title: 'AI/ML Solutions',
    description: 'Unlock business growth with AI and machine learning. From chatbots to intelligent automation, we build solutions that deliver smarter decisions.',
  },
  {
    icon: <FaIdBadge aria-label="Digital Portfolio" title="Digital Portfolio" />,
    title: 'Digital Portfolio Development',
    description: 'Your unique career story, beautifully told online. We build stunning digital portfolios that make a powerful first impression. Using React, we create fast, responsive, and SEO optimized sites that highlight your skills and get you truly noticed.',
  },
];

const otherServices = [
  {
    icon: <FaImage aria-label="Poster Making Icon" title="Poster Making" />,
    title: 'Poster Making',
    description: 'We design high-impact posters that capture attention and communicate your message effectively.'
  },
  {
    icon: <FaPenNib aria-label="Logo Making Icon" title="Logo Making" />,
    title: 'Logo Making',
    description: 'We create unique, memorable logos that perfectly represent your brand’s identity.'
  },
  {
    icon: <FaMagic aria-label="Logo Enhancement Icon" title="Logo Enhancement" />,
    title: 'Logo Enhancement',
    description: 'We refresh and refine your existing logo to give it a modern, polished look.'
  },
  {
    icon: <FaBullhorn aria-label="Branding Icon" title="Branding" />,
    title: 'Branding',
    description: 'We develop a complete brand identity that tells your story and connects with your audience.'
  }
];

const Services = () => {
  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  return (
    <>
      <section
        id="services"
        className="bg-[#0A0A23] py-24 px-6 md:px-16 lg:px-32 scroll-mt-24 snap-start"
      >
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-700 to-blue-600 mb-8">
            Our Services
          </h2>

          {/* SEO-focused intro */}
          <div className="mb-12">
            <motion.p
              className="mx-auto max-w-3xl text-base sm:text-lg md:text-xl font-medium text-white/90"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.7 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              data-aos="fade-up"
              data-aos-delay="100"
            >
              At <strong className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-blue-500 to-cyan-400">MASRIQ NextTech Solutions</strong>, 
              we specialize in <span className="font-semibold text-blue-400">web development</span>, 
              <span className="font-semibold text-purple-400"> app development</span>, 
              <span className="font-semibold text-cyan-400"> cloud platforms</span>, and 
              <span className="font-semibold text-purple-400"> AI/ML solutions</span>.  
              Our team combines creativity, technology, and business insight to deliver digital products that boost visibility, engagement, and ROI.
            </motion.p>
          </div>

          {/* Service Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {services.map((service, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.7, ease: 'easeOut', delay: index * 0.15 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 shadow-xl border border-purple-300 hover:border-purple-100 transition hover:scale-105 hover:shadow-2xl group"
              >
                <div className="text-4xl text-white mb-4 group-hover:scale-110 group-hover:text-purple-500 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-cyan-400 text-sm leading-relaxed group-hover:text-cyan-300 transition-colors duration-300">
                  {service.description}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-[#0A0A23] py-20 px-6 md:px-16 lg:px-32 scroll-mt-24 snap-start">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600 mb-8">
            Our Other Services
          </h2>
          <div className="mb-10">
            <motion.p
              className="mx-auto max-w-2xl text-base sm:text-lg md:text-xl font-medium text-white/80"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.7 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              data-aos="fade-up"
              data-aos-delay="100"
            >
              Explore our additional offerings designed to help your business grow and thrive in the digital age.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {otherServices.map((service, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.7, ease: 'easeOut', delay: index * 0.15 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 shadow-xl border border-cyan-300 hover:border-cyan-100 transition hover:scale-105 hover:shadow-2xl group"
              >
                <div className="text-4xl text-cyan-400 mb-4 group-hover:scale-110 group-hover:text-purple-500 transition-transform duration-300">
                  {service.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-cyan-400 text-sm leading-relaxed group-hover:text-cyan-300 transition-colors duration-300">
                  {service.description}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
